import { AntDesign } from "@expo/vector-icons";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, View, Pressable, FlatList, TextInput } from "react-native";

import { generateCounterpartiesXlsx } from "@/utils/management/ExportCounterpartiesData";
import { getDisplayedPages } from "@/utils/management/Pages";

interface CounterpartiesProps {
  data: any;
}

export default function Counterparties(props: CounterpartiesProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const pageLimit = 15;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const renderPageIndicator = ({ item }: { item: number }) => (
    <Pressable onPress={() => handlePageChange(item)} className="mx-1">
      <View
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          currentPage === item ? "bg-purple1" : "bg-gray"
        }`}
      >
        <Text
          className={`${currentPage === item ? "text-white" : "text-black1"}`}
        >
          {item}
        </Text>
      </View>
    </Pressable>
  );

  const filteredCounterparties = (props.data.counterParties ?? []).filter(
    (counterparty) =>
      counterparty.profile?.name
        ?.toLowerCase()
        .includes(searchQuery?.toLowerCase()),
  );

  const paginatedCounterparties = searchQuery
    ? filteredCounterparties
    : (props.data.counterParties ?? []).slice(
        (currentPage - 1) * pageLimit,
        currentPage * pageLimit,
      );

  const totalPages = Math.ceil(
    (searchQuery ? filteredCounterparties : props.data.counterParties || 1)
      .length / pageLimit,
  );

  const addCustomerInformationIntoCounterparties = (counterparties: any[]) => {
    return counterparties.map((counterparty) => {
      const application =
        props.data.application?.customerDetailsCorporation ??
        props.data.application?.customerDetailsIndividual;

      const modifiedCounterparty = { ...counterparty, customer: {} };

      modifiedCounterparty.customer.application = application;
      return {
        ...modifiedCounterparty,
      };
    });
  };

  return (
    <View className="w-full flex-col items-center">
      <View className="w-full flex-row justify-between my-4">
        <View className="items-center">
          <View className="absolute left-4 top-2.5">
            <AntDesign name="search1" size={16} color="#BDBDBD" />
          </View>
          <TextInput
            className="border text-sm border-midgray rounded-full pl-10 py-2 text-base"
            placeholder={t("common:terms.searchcounterparty")}
            placeholderTextColor="#BDBDBD"
            onChangeText={(text) => setSearchQuery(text)}
          />
        </View>
        <Pressable
          className="flex-col lg:flex-row gap-x-2 items-center justify-center"
          onPress={() =>
            generateCounterpartiesXlsx(
              addCustomerInformationIntoCounterparties(
                props.data.counterParties,
              ),
              (props.data.application?.customerDetailsCorporation
                ?.registeredName ||
                props.data.application?.customerDetailsIndividual?.firstName) ??
                "izziwallet",
              t,
            )
          }
        >
          <AntDesign name="download" size={18} color="#BDBDBD" />
          <Text className="text-midgray text-xs lg:text-sm hover:text-darkergray">
            {t("common:terms.exportcounterparties")}
          </Text>
        </Pressable>
      </View>
      <View className="w-full">
        <View className="py-2 px-4 w-full flex-row">
          <View className="w-1/4 items-start">
            <Text className="font-thin text-lg text-darkergray">
              {t("management/counterparties:board.name")}
            </Text>
          </View>
          <View className="w-1/4 items-start">
            <Text className="font-thin text-lg text-darkergray">
              {t("management/counterparties:board.type")}
            </Text>
          </View>
          <View className="w-1/4 items-start">
            <Text className="font-thin text-lg text-darkergray">
              {t("management/counterparties:board.rail")}
            </Text>
          </View>
          <View className="w-1/4 items-start">
            <Text className="font-thin text-lg text-darkergray">
              {t("management/counterparties:board.country")}
            </Text>
          </View>
        </View>
        <FlatList
          data={paginatedCounterparties}
          contentContainerClassName="gap-2 p-2 flex-col"
          renderItem={({ item }) => (
            <View className="bg-gray hover:bg-lightgray border border-gray rounded-lg py-2 px-4 w-full flex-row">
              <View className="w-1/4 items-start">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="font-thin text-sm text-black"
                >
                  {item.profile?.name ?? "-"}
                </Text>
              </View>
              <View className="w-1/4 items-start">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="font-thin text-sm text-black"
                >
                  {item.profile?.profileType ?? "-"}
                </Text>
              </View>
              <View className="w-1/4 items-start">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="font-thin text-sm text-black"
                >
                  {item.rails[0] ?? "-"}
                </Text>
              </View>
              <View className="w-1/4 items-start">
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  className="font-thin text-sm text-black"
                >
                  {item.profile?.address?.countryCode ?? "-"}
                </Text>
              </View>
            </View>
          )}
        />
        <FlatList
          horizontal
          data={getDisplayedPages(totalPages, currentPage)}
          renderItem={renderPageIndicator}
          keyExtractor={(item, index) => index.toString()}
          contentContainerClassName="mt-4 w-full items-center justify-center"
        />
      </View>
    </View>
  );
}
